import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input } from './FormControls'
import SpecializedModulePanel from './SpecializedModulePanel'

// =======================================================================
// SetQuantityModal — Wave 6 Phase 1 (Manual Quantity only).
//
// STRICT RULE: this component performs NO quantity calculation and NO
// financial calculation. It only collects a number + optional note and
// hands it to the existing recapture_quantity_source() RPC exactly as it
// is today — no new parameters, no schema changes, no trigger changes.
// Canvas Takeoff is not reimplemented here; it links out to the existing
// TakeoffPage, which continues to work completely unchanged.
// =======================================================================

const TABS = { MANUAL: 'manual', CANVAS: 'canvas', SPECIALIZED: 'specialized' }

export default function SetQuantityModal({ boqItem, tenderId, revisionId, isFrozen, onClose, onApplied }) {
  const [tab, setTab] = useState(TABS.MANUAL)
  const [quantity, setQuantity] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState(null)
  const [applying, setApplying] = useState(false)
  const [currentSource, setCurrentSource] = useState(undefined) // undefined=loading, null=none

  useEffect(() => {
    supabase
      .from('quantity_source')
      .select('source_type, captured_value')
      .eq('boq_item_id', boqItem.id)
      .eq('is_active', true)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) return setError(error.message)
        setCurrentSource(data)
      })
  }, [boqItem.id])

  const parsedQty = Number(quantity)
  const qtyError =
    quantity.trim() === '' ? null // not yet validated (no error shown until Apply attempted)
    : Number.isNaN(parsedQty) ? 'Enter a valid number.'
    : parsedQty < 0 ? 'Quantity cannot be negative.'
    : null

  async function handleApply() {
    setError(null)
    if (quantity.trim() === '') return setError('Quantity is required.')
    if (Number.isNaN(parsedQty)) return setError('Enter a valid number.')
    if (parsedQty < 0) return setError('Quantity cannot be negative.')

    setApplying(true)
    const { data: authData } = await supabase.auth.getUser()
    // Existing RPC, existing signature, existing behavior — unchanged.
    const { error: rpcError } = await supabase.rpc('recapture_quantity_source', {
      p_boq_item_id: boqItem.id,
      p_source_type: 'manual_entry',
      p_captured_value: parsedQty,
      p_takeoff_item_id: null,
      p_captured_by: authData.user?.id,
    })
    setApplying(false)
    if (rpcError) {
      setError(rpcError.message)
    } else {
      onApplied()
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,16,23,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) e.currentTarget.dataset.downOnBackdrop = 'true' }}
      onMouseUp={(e) => {
        // Only close if BOTH mousedown and mouseup happened directly on
        // the backdrop itself. This prevents an accidental close when a
        // text selection/drag started inside the modal ends up outside
        // its bounds (e.g. while selecting text to copy).
        if (e.target === e.currentTarget && e.currentTarget.dataset.downOnBackdrop === 'true') onClose()
        e.currentTarget.dataset.downOnBackdrop = 'false'
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 10, width: tab === TABS.SPECIALIZED ? 'min(720px, 95vw)' : 'min(520px, 92vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}
      >
        {/* Header */}
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary, textTransform: 'uppercase', letterSpacing: 0.3 }}>Set Quantity</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        {/* BOQ item context */}
        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${colors.border}`, background: '#FAFBFC', flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>{boqItem.item_no ? `${boqItem.item_no} — ` : ''}{boqItem.description}</div>
          <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
            Unit: <strong style={{ color: colors.textSecondary }}>{boqItem.unit}</strong>
            {currentSource === undefined ? null : currentSource ? (
              <span> · Current source: <strong style={{ color: colors.primary }}>{currentSource.source_type === 'manual_entry' ? 'Manual' : currentSource.source_type === 'takeoff_derived' ? 'Canvas' : currentSource.source_type}</strong> ({formatNumber(currentSource.captured_value)} {boqItem.unit})</span>
            ) : (
              <span> · No quantity set yet</span>
            )}
          </div>
        </div>

        {isFrozen ? (
          <div style={{ padding: 18 }}>
            <div style={{ padding: '10px 14px', background: colors.warningBg, color: colors.warningText, borderRadius: 6, fontSize: 13 }}>
              🔒 This revision is frozen — quantity cannot be modified.
            </div>
            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={onClose}>Close</Button>
            </div>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: `1px solid ${colors.border}`, flexShrink: 0 }}>
              {[{ key: TABS.MANUAL, label: 'Manual Quantity' }, { key: TABS.CANVAS, label: 'Canvas Takeoff' }, { key: TABS.SPECIALIZED, label: 'Engineering Modules' }].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  style={{
                    flex: 1, padding: '10px 0', background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: 700, color: tab === t.key ? colors.primary : colors.textMuted,
                    borderBottom: tab === t.key ? `2px solid ${colors.primary}` : '2px solid transparent',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div style={{ padding: 18, overflowY: 'auto', flex: 1 }}>
              {tab === TABS.MANUAL ? (
                <>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 5 }}>Quantity</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    <Input
                      type="number" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)}
                      placeholder="0.00" style={{ flex: 1, fontSize: 15 }} autoFocus
                    />
                    <span style={{ fontSize: 13, fontWeight: 600, color: colors.textSecondary, minWidth: 40 }}>{boqItem.unit}</span>
                  </div>
                  {qtyError && <div style={{ fontSize: 12, color: colors.danger, marginTop: -10, marginBottom: 14 }}>{qtyError}</div>}

                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 5 }}>Note (optional)</label>
                  <textarea
                    value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Based on preliminary structural takeoff"
                    style={{ width: '100%', minHeight: 60, padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
                  />

                  {quantity.trim() !== '' && !qtyError && (
                    <div style={{ marginTop: 16, padding: '12px 14px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8 }}>
                      <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.3 }}>Result</div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: colors.primary, marginTop: 2 }}>{formatNumber(parsedQty)} {boqItem.unit}</div>
                      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>Source: Manual Quantity</div>
                    </div>
                  )}
                </>
              ) : tab === TABS.CANVAS ? (
                <div style={{ textAlign: 'center', padding: '30px 10px' }}>
                  <p style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 16 }}>
                    Measure this item on the drawing using the Takeoff Canvas — length, area, count, and more.
                  </p>
                  <Button
                    onClick={() => window.open(`/estimation/tender/${tenderId}/revision/${revisionId}/takeoff`, '_blank')}
                  >
                    Open Takeoff Canvas →
                  </Button>
                </div>
              ) : (
                <SpecializedModulePanel
                  boqItem={boqItem}
                  tenderId={tenderId}
                  onApplied={onApplied}
                  onError={setError}
                />
              )}
            </div>

            {error && (
              <div style={{ margin: '0 18px 12px', padding: '8px 12px', background: colors.dangerBg, color: colors.danger, borderRadius: 6, fontSize: 12 }}>
                ⚠ {error}
              </div>
            )}

            {tab === TABS.MANUAL && (
              <div style={{ padding: '12px 18px', borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
                <Button variant="secondary" onClick={onClose} disabled={applying}>Cancel</Button>
                <Button onClick={handleApply} disabled={applying || quantity.trim() === '' || !!qtyError}>
                  {applying ? 'Applying…' : 'Apply Quantity'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
